import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  Button,
  TextField,
  Typography,
  Box,
  Avatar,
  Divider,
  IconButton,
  Paper,
  Chip,
  Stack,
  alpha,
  useTheme
} from '@mui/material';
import {
  Edit,
  Phone,
  Email,
  Business,
  Work,
  PhotoCamera,
  Badge,
  VerifiedUser,
  BusinessCenter,
  AccountCircle
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  background: theme.palette.mode === 'dark' 
    ? theme.palette.background.paper 
    : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)'
    : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  border: theme.palette.mode === 'dark' 
    ? `1px solid ${theme.palette.divider}`
    : `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 10px 15px -3px rgba(0, 0, 0, 0.4), 0 4px 6px -2px rgba(0, 0, 0, 0.3)'
      : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  }
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: '12px',
  background: theme.palette.mode === 'dark'
    ? alpha(theme.palette.primary.main, 0.08)
    : alpha(theme.palette.primary.main, 0.04),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    background: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.primary.main, 0.06),
    transform: 'translateX(4px)',
  }
}));

const ProfileHeader = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.dark, 0.15)} 100%)`
    : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
  borderRadius: '16px',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    right: 0,
    width: '200px',
    height: '200px',
    background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 70%)`,
    borderRadius: '50%',
    transform: 'translate(30%, -30%)',
  }
}));

const Profile = () => {
  const theme = useTheme();
  const [photoUrl, setPhotoUrl] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    email: '',
    phone: '',
    bu: '',
    role: '',
    department: '',
    jobPosition: '',
    buCode: '',
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const parsedToken = JSON.parse(atob(token.split('.')[1])); 
        setUserData(parsedToken);
        setFormData({
          fullName: parsedToken.nama_lengkap || '',
          username: parsedToken.username || '',
          email: parsedToken.email || '',
          phone: parsedToken.nik || '',
          bu: parsedToken.bu || '',
          role: parsedToken.role || '',
          department: parsedToken.department_name || '',
          jobPosition: parsedToken.job_position?.name || '',
          buCode: parsedToken.bu_code || '',
          photo: parsedToken.profile_url || '',
        });
      } catch (error) {
        console.error("Failed to parse token:", error);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData((prev) => ({
          ...prev,
          photo: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Authentication required. Please login again.');
      return;
    }

    try {
      const data = new FormData();
      data.append('fullName', formData.fullName);
      data.append('username', formData.username);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('bu', formData.bu);
      data.append('role', formData.role);
      data.append('department', formData.department);
      data.append('jobPosition', formData.jobPosition);
      data.append('buCode', formData.buCode);
      // If photo is a base64 string, convert to Blob
      if (formData.photo && formData.photo.startsWith('data:image')) {
        const arr = formData.photo.split(','), mime = arr[0].match(/:(.*?);/)[1], bstr = atob(arr[1]), n = bstr.length, u8arr = new Uint8Array(n);
        for (let i = 0; i < n; i++) u8arr[i] = bstr.charCodeAt(i);
        data.append('photo', new Blob([u8arr], { type: mime }), 'profile-photo.png');
      } else if (formData.photo) {
        data.append('photo', formData.photo);
      }

      const response = await fetch('https://apidashboard.eurekagroup.id/employee/profile', {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: data
      });

      if (response.ok) {
        const result = await response.json();
        alert('Profile updated successfully!');
        setIsEditing(false);
        // Optionally update local state with new profile data
        setUserData(result);
        setFormData((prev) => ({ ...prev, ...result }));
      } else {
        const error = await response.json();
        alert('Failed to update profile: ' + (error.message || response.statusText));
      }
    } catch (err) {
      alert('Error updating profile: ' + err.message);
    }
  };

  useEffect(() => {
    const fetchPhoto = async () => {
      if (!formData.photo) return;

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('Token not found in local storage.');
          return;
        }

        const photoUrl = formData.photo.startsWith('http')
          ? formData.photo
          : `https://apidashboard.eurekagroup.id${formData.photo}`;

        const response = await fetch(photoUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);
          setPhotoUrl(imageUrl);
        } else {
          console.error('Failed to fetch profile photo:', response.statusText);
          setPhotoUrl(formData.photo);
        }
      } catch (error) {
        console.error('Error fetching photo:', error);
        setPhotoUrl(formData.photo);
      }
    };

    fetchPhoto();
  }, [formData.photo]);

  const displayPhoto = photoUrl || formData.photo;

  return (
    <Box 
      sx={{ 
        bgcolor: 'background.default',
        minHeight: '100vh',
        pt: 4,
        pb: 8
      }}
    >
      <Container maxWidth="lg">
        {/* Profile Header */}
        <ProfileHeader>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm="auto">
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Avatar
                  src={displayPhoto}
                  sx={{
                    width: { xs: 120, sm: 140 },
                    height: { xs: 120, sm: 140 },
                    border: `4px solid ${theme.palette.background.paper}`,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  }}
                />
                <IconButton
                  component="label"
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    bgcolor: 'primary.main',
                    color: 'white',
                    border: `3px solid ${theme.palette.background.paper}`,
                    width: 40,
                    height: 40,
                    '&:hover': { 
                      bgcolor: 'primary.dark',
                      transform: 'scale(1.1)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <PhotoCamera sx={{ fontSize: 20 }} />
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </IconButton>
              </Box>
            </Grid>
            <Grid item xs={12} sm>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Typography variant="h4" fontWeight="bold" color="text.primary">
                    {formData.fullName || 'User Name'}
                  </Typography>
                  {userData?.role === 'superadmin' && (
                    <Chip
                      icon={<VerifiedUser />}
                      label="Super Admin"
                      color="primary"
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Stack>
                <Typography variant="h6" color="text.secondary" mb={2}>
                  {formData.jobPosition || formData.department || 'Position'}
              </Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
                  <Chip
                    icon={<Business />}
                    label={formData.bu || 'Business Unit'}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      borderColor: 'primary.main',
                      color: 'text.primary'
                    }}
                  />
                  <Chip
                    icon={<Work />}
                    label={formData.department || 'Department'}
                    variant="outlined"
                    size="small"
                    sx={{ 
                      borderColor: 'primary.main',
                      color: 'text.primary'
                    }}
                  />
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} sm="auto">
              <Button
                startIcon={<Edit />}
                onClick={() => setIsEditing(!isEditing)}
                variant={isEditing ? "outlined" : "contained"}
                size="large"
                sx={{
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  boxShadow: isEditing ? 'none' : '0 4px 12px rgba(40, 134, 222, 0.3)',
                  '&:hover': {
                    boxShadow: isEditing ? 'none' : '0 6px 16px rgba(40, 134, 222, 0.4)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </Button>
            </Grid>
          </Grid>
        </ProfileHeader>

        <Grid container spacing={3}>
          {/* Left Column - Contact Info */}
          <Grid item xs={12} md={4}>
            <StyledCard sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={3} color="text.primary">
                Contact Information
              </Typography>
              
              <Stack spacing={2.5}>
                <InfoCard>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: '10px',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Email sx={{ color: 'primary.main', fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Email
                      </Typography>
                      <Typography variant="body2" fontWeight={500} color="text.primary">
                        {formData.email || formData.username || 'N/A'}
                      </Typography>
                    </Box>
                  </Stack>
                </InfoCard>

                <InfoCard>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: '10px',
                        bgcolor: alpha(theme.palette.success.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Phone sx={{ color: 'success.main', fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        NIK / Phone
                      </Typography>
                      <Typography variant="body2" fontWeight={500} color="text.primary">
                        {formData.phone || 'N/A'}
                      </Typography>
                </Box>
                  </Stack>
                </InfoCard>

                <InfoCard>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: '10px',
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Business sx={{ color: 'info.main', fontSize: 24 }} />
                </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Business Unit
                      </Typography>
                      <Typography variant="body2" fontWeight={500} color="text.primary">
                        {formData.bu || 'N/A'}
                      </Typography>
                </Box>
                  </Stack>
                </InfoCard>

                <InfoCard>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: '10px',
                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Work sx={{ color: 'warning.main', fontSize: 24 }} />
                </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        Department
                      </Typography>
                      <Typography variant="body2" fontWeight={500} color="text.primary">
                        {formData.department || 'N/A'}
                      </Typography>
              </Box>
                  </Stack>
                </InfoCard>
              </Stack>
            </StyledCard>
          </Grid>

          {/* Right Column - Profile Details */}
          <Grid item xs={12} md={8}>
            <StyledCard sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight="bold" mb={3} color="text.primary">
                  Profile Information
                </Typography>

              {isEditing ? (
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Full Name"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Username"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="NIK"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Business Unit"
                        name="bu"
                        value={formData.bu}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Department"
                        name="department"
                        value={formData.department}
                        onChange={handleChange}
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setIsEditing(false)}
                          sx={{ borderRadius: '10px', textTransform: 'none', px: 3 }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          type="submit"
                          sx={{ borderRadius: '10px', textTransform: 'none', px: 3 }}
                        >
                          Save Changes
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              ) : (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <InfoCard>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                          <AccountCircle sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                            Personal Information
                          </Typography>
                        </Stack>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              Full Name
                            </Typography>
                            <Typography variant="body1" fontWeight={600} color="text.primary">
                              {formData.fullName || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              Username
                            </Typography>
                            <Typography variant="body1" fontWeight={600} color="text.primary">
                              {formData.username || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </InfoCard>
                    </Grid>

                    <Grid item xs={12}>
                      <InfoCard>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                          <BusinessCenter sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                            Work Information
                          </Typography>
                        </Stack>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              Job Position
                            </Typography>
                            <Typography variant="body1" fontWeight={600} color="text.primary">
                              {formData.jobPosition || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              Department
                            </Typography>
                            <Typography variant="body1" fontWeight={600} color="text.primary">
                              {formData.department || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              Business Unit
                            </Typography>
                            <Typography variant="body1" fontWeight={600} color="text.primary">
                              {formData.bu || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              BU Code
                            </Typography>
                            <Typography variant="body1" fontWeight={600} color="text.primary">
                              {formData.buCode || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </InfoCard>
                    </Grid>

                    <Grid item xs={12}>
                      <InfoCard>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                          <Badge sx={{ fontSize: 18, color: 'text.secondary' }} />
                          <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                            Account Information
                          </Typography>
                        </Stack>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              Role
                            </Typography>
                            <Chip
                              label={formData.role || 'N/A'}
                              color="primary"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                              User ID
                            </Typography>
                            <Typography variant="body2" fontWeight={500} color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                              {userData?.userId || 'N/A'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </InfoCard>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </StyledCard>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Profile;